import { Link } from "react-router-dom";
import { FileText, ChevronLeft } from "lucide-react";

export default function CookiePolicyPage() {
  return (
    <div className="py-12 sm:py-16" data-testid="cookie-policy-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-6 transition-colors">
          <ChevronLeft className="w-3 h-3" /> Volver al inicio
        </Link>

        <div className="flex items-center gap-3 mb-10">
          <div className="w-10 h-10 bg-slate-900 rounded-sm flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="heading-industrial text-2xl sm:text-3xl font-extrabold text-slate-900 uppercase tracking-tight" data-testid="cookie-policy-title">
              Politica de Cookies
            </h1>
            <p className="text-xs text-slate-400 uppercase tracking-wider mt-0.5">Uso de cookies en ascauto.com</p>
          </div>
        </div>

        {/* 1. Que son */}
        <Section title="1. Que son las cookies">
          <p className="text-sm text-slate-600 leading-relaxed">
            Las cookies son pequeños archivos de texto que los sitios web instalan en el navegador del usuario al visitarlos. Sirven para almacenar información sobre la visita, recordar preferencias, mejorar la experiencia de navegación y, en algunos casos, recopilar datos estadísticos anónimos sobre el uso del sitio.
          </p>
        </Section>

        {/* 2. Quien las utiliza */}
        <Section title="2. Responsable del uso de cookies">
          <p className="text-sm text-slate-600 leading-relaxed">
            El responsable del uso de cookies en este sitio web es <strong>Ascauto, S.A.</strong> (CIF A79668307), con domicilio en C. de Simón Hernández, 71, 28937 Móstoles, Madrid. Puedes contactarnos en{" "}
            <a href="mailto:RGPD@ascauto.com" className="text-red-600 underline hover:text-red-700">RGPD@ascauto.com</a>.
          </p>
        </Section>

        {/* 3. Tipos */}
        <Section title="3. Tipos de cookies que utilizamos">
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            A continuacion detallamos las cookies que puede utilizar este sitio web:
          </p>

          <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2">3.1. Cookies tecnicas (necesarias)</h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            Son imprescindibles para el funcionamiento basico del sitio web. Permiten la navegacion, el uso de funcionalidades esenciales y la gestion de sesiones de usuario. No requieren consentimiento.
          </p>
          <CookieTable cookies={[
            { name: "auth_token", provider: "ascauto.com", purpose: "Mantener la sesion del usuario autenticado en el panel de gestion", duration: "Sesion del navegador", type: "Tecnica" },
            { name: "cookie_consent", provider: "ascauto.com", purpose: "Almacenar las preferencias de cookies del usuario", duration: "12 meses", type: "Tecnica" },
          ]} />

          <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2 mt-6">3.2. Cookies de preferencias</h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            Permiten recordar las opciones seleccionadas por el usuario, como el idioma, el centro preferido o configuraciones de visualizacion.
          </p>
          <CookieTable cookies={[
            { name: "user_preferences", provider: "ascauto.com", purpose: "Recordar preferencias de idioma, centro y configuracion del sitio", duration: "12 meses", type: "Preferencias" },
          ]} />

          <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider mb-2 mt-6">3.3. Cookies analiticas</h3>
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            Nos permiten analizar el uso del sitio web, medir la audiencia, conocer las paginas mas visitadas y detectar posibles problemas de navegacion. Los datos recogidos son anonimos y agregados.
          </p>
          <CookieTable cookies={[
            { name: "_ga / _gid", provider: "Google Analytics", purpose: "Analisis estadistico anonimo del trafico y comportamiento de los usuarios", duration: "24 meses / 24 horas", type: "Analitica" },
          ]} />
        </Section>

        {/* 4. Gestion */}
        <Section title="4. Como gestionar las cookies">
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            Puedes gestionar tus preferencias de cookies de varias formas:
          </p>
          <ul className="list-disc list-inside text-sm text-slate-600 space-y-2 ml-2">
            <li><strong>Banner de cookies:</strong> Al acceder por primera vez al sitio web, se muestra un banner donde puedes aceptar, rechazar o configurar las cookies. Puedes modificar tu eleccion en cualquier momento desde el enlace "Configurar cookies" del pie de pagina.</li>
            <li><strong>Configuracion del navegador:</strong> Todos los navegadores permiten gestionar las cookies desde sus opciones de configuracion. Consulta la ayuda de tu navegador:
              <ul className="list-none ml-4 mt-1 space-y-1 text-xs">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-red-600 underline hover:text-red-700">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/es/kb/cookies-informacion-que-los-sitios-web-guardan-en-" target="_blank" rel="noopener noreferrer" className="text-red-600 underline hover:text-red-700">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-red-600 underline hover:text-red-700">Safari</a></li>
                <li><a href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-red-600 underline hover:text-red-700">Microsoft Edge</a></li>
              </ul>
            </li>
          </ul>
          <p className="text-sm text-slate-600 leading-relaxed mt-3">
            Ten en cuenta que la desactivacion de cookies tecnicas o necesarias puede impedir el correcto funcionamiento de determinadas funcionalidades del sitio web.
          </p>
        </Section>

        {/* 5. Mas info */}
        <Section title="5. Mas informacion">
          <p className="text-sm text-slate-600 leading-relaxed">
            Para mas informacion sobre el uso de cookies y el tratamiento de tus datos personales, consulta nuestra{" "}
            <Link to="/politica-de-privacidad" className="text-red-600 underline hover:text-red-700 font-medium">Politica de Privacidad</Link>{" "}
            o contacta con nosotros en{" "}
            <a href="mailto:RGPD@ascauto.com" className="text-red-600 underline hover:text-red-700 font-medium">RGPD@ascauto.com</a>.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed mt-2">
            Si deseas obtener información general sobre cookies, puedes visitar{" "}
            <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-red-600 underline hover:text-red-700">www.aboutcookies.org</a>.
          </p>
        </Section>

        <p className="text-xs text-slate-400 text-center mt-10 pt-6 border-t border-slate-100">Ultima actualizacion: febrero 2026 | Ascauto, S.A. | CIF A79668307</p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3 pb-2 border-b border-slate-200">{title}</h2>
      <div>{children}</div>
    </div>
  );
}

function CookieTable({ cookies }) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-sm">
      <table className="w-full text-sm text-left">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="py-2.5 px-3 text-xs font-bold text-slate-800 uppercase tracking-wider">Cookie</th>
            <th className="py-2.5 px-3 text-xs font-bold text-slate-800 uppercase tracking-wider">Proveedor</th>
            <th className="py-2.5 px-3 text-xs font-bold text-slate-800 uppercase tracking-wider">Finalidad</th>
            <th className="py-2.5 px-3 text-xs font-bold text-slate-800 uppercase tracking-wider">Duracion</th>
          </tr>
        </thead>
        <tbody className="text-slate-600">
          {cookies.map(c => (
            <tr key={c.name} className="border-b border-slate-100 last:border-0">
              <td className="py-2 px-3 font-mono text-xs">{c.name}</td>
              <td className="py-2 px-3 text-xs">{c.provider}</td>
              <td className="py-2 px-3 text-xs">{c.purpose}</td>
              <td className="py-2 px-3 text-xs whitespace-nowrap">{c.duration}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
